








import Vue from 'vue';
import { mapMutations, mapState } from 'vuex';
import { PersistentNotification } from '../lib/interfaces/persistentNotification';
import Pusher from 'pusher-js';
import { createPusher } from '../lib/pusher';

const initialNotificationState = () => ({
  type: undefined,
  title: undefined,
  text: undefined,
  link: {
    text: undefined,
    url: undefined,
    external: undefined
  }
});

export default Vue.extend({
  name: 'PersistentNotification',

  props: {
    containerClass: { type: String, required: false }
  },

  data() {
    return {
      notification: initialNotificationState()
    };
  },

  computed: {
    ...mapState('components/persistent-notification', ['queuedNotification']),
    ...mapState('user', { userId: 'id', clientId: 'clientId' })
  },

  watch: {
    // A new page was loaded (using the same layout). If a notification has
    // been queued (i.e. was set to show on the next page load), show
    // the notification and clear the notification from the store (the queue).
    // If no notification was queued, close the current notification (e.g. close
    // the notification from the previous page).
    '$route.path'() {
      if (this.queuedNotification.text) this.useQueuedNotification();
      else this.close();
    },
    userId: {
      handler(id) {
        if (id) this.setUpPusher();
      },
      immediate: true
    }
  },

  created() {
    // A new layout has been loaded so a new PersistentNotification instance
    // has been created. As with a new page load, if a notification has been
    // queued, show the notification and clear the notification from the store.
    if (this.queuedNotification.text) this.useQueuedNotification();

    this.$nuxt.$on(
      'show-on-current-page:persistent-notification',
      this.setLocalNotification
    );
    this.$nuxt.$on(
      'show-on-next-page:persistent-notification',
      this.setQueuedNotification
    );
    this.$nuxt.$on('close:persistent-notification', this.close);
  },

  beforeDestroy() {
    this.$nuxt.$off('show-on-current-page:persistent-notification');
    this.$nuxt.$off('show-on-next-page:persistent-notification');
    this.$nuxt.$off('close:persistent-notification');
  },

  methods: {
    ...mapMutations('components/persistent-notification', {
      setQueuedNotification: 'setQueuedNotification',
      clearQueuedNotification: 'resetQueuedNotification'
    }),

    close() {
      this.resetLocalNotification();
    },

    resetLocalNotification() {
      this.setLocalNotification(initialNotificationState());
    },

    setLocalNotification(notification: PersistentNotification): void {
      this.notification.type = notification.type;
      this.notification.title = notification.title;
      this.notification.text = notification.text;
      this.notification.link.text = notification.link?.text;
      this.notification.link.url = notification.link?.url;
      this.notification.link.external = notification.link?.external;
      this.notification.route = notification.route;
    },

    useQueuedNotification() {
      if (!this.showOnCurrentRoute(this.queuedNotification)) return;
      this.setLocalNotification(this.queuedNotification);
      this.clearQueuedNotification();
    },

    setUpPusher() {
      const pusher = createPusher(this.userId, this.clientId);
      const handleMessage = (notification: PersistentNotification) => {
        if (this.showOnCurrentRoute(notification))
          this.$nuxt.$emit(
            'show-on-current-page:persistent-notification',
            notification
          );
        else
          this.$nuxt.$emit(
            'show-on-next-page:persistent-notification',
            notification
          );
      };
      pusher.userChannel.bind('persistent-notification', handleMessage);
      pusher.clientChannel.bind('persistent-notification', handleMessage);
    },

    showOnCurrentRoute(notification) {
      return !!(
        !notification.route || this.$route.path.startsWith(notification.route)
      );
    }
  }
});
